import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  History as HistoryIcon, 
  Loader2,
  Search,
  FileCheck,
  ChevronDown,
  ChevronRight,
  User
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export default function HistoryPage() {
  const { user } = useAuth();
  const [objectives, setObjectives] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSellers, setExpandedSellers] = useState({});
  
  // Filters
  const [search, setSearch] = useState("");
  const [filterConcession, setFilterConcession] = useState("all");
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());

  useEffect(() => {
    fetchData();
  }, [filterYear, filterConcession]);

  const fetchData = async () => {
    try {
      const params = { status: "signed" };
      if (filterYear) params.year = parseInt(filterYear);
      if (filterConcession !== "all" && user?.role === "admin") {
        params.concession_id = filterConcession;
      }

      const [objectivesRes, concessionsRes] = await Promise.all([
        api.get("/objectives", { params }),
        user?.role === "admin" ? api.get("/concessions") : Promise.resolve({ data: [] })
      ]);
      setObjectives(objectivesRes.data);
      setConcessions(concessionsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const monthNames = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  // Filter objectives by search
  const filteredObjectives = objectives.filter(obj => 
    obj.seller_name?.toLowerCase().includes(search.toLowerCase()) ||
    obj.concession_name?.toLowerCase().includes(search.toLowerCase())
  );

  // Group objectives by seller
  const groupedBySeller = filteredObjectives.reduce((acc, obj) => {
    const key = obj.seller_id;
    if (!acc[key]) {
      acc[key] = {
        seller_id: obj.seller_id,
        seller_name: obj.seller_name,
        concession_name: obj.concession_name,
        objectives: [],
        total_vn: 0,
        total_vo: 0,
        total_financing: 0,
        count: 0
      };
    }
    acc[key].objectives.push(obj);
    acc[key].total_vn += obj.vn_target || 0;
    acc[key].total_vo += obj.vo_target || 0;
    acc[key].total_financing += obj.financing_target || 0;
    acc[key].count += 1;
    return acc;
  }, {});

  // Convert to array and sort by seller name
  const sellersList = Object.values(groupedBySeller).sort((a, b) => 
    a.seller_name.localeCompare(b.seller_name)
  );

  // Sort objectives by month within each seller
  sellersList.forEach(seller => {
    seller.objectives.sort((a, b) => a.month - b.month);
  });

  const toggleSeller = (sellerId) => {
    setExpandedSellers(prev => ({
      ...prev,
      [sellerId]: !prev[sellerId]
    }));
  };

  // Calculate totals
  const totalObjectives = filteredObjectives.length;
  const totalVN = filteredObjectives.reduce((acc, obj) => acc + (obj.vn_target || 0), 0);
  const totalVO = filteredObjectives.reduce((acc, obj) => acc + (obj.vo_target || 0), 0);

  if (loading) {
    return (
      <Layout title="Historique">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E30613]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Historique des objectifs signés">
      {/* Filters */}
      <div className="card-dashboard mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Rechercher par vendeur ou concession..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="history-search"
            />
          </div>
          {user?.role === "admin" && (
            <Select value={filterConcession} onValueChange={setFilterConcession}>
              <SelectTrigger className="w-48" data-testid="filter-concession">
                <SelectValue placeholder="Toutes concessions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes concessions</SelectItem>
                {concessions.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-28" data-testid="filter-year">
              <SelectValue placeholder="Année" />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map((year) => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card-kpi border-gray-300">
          <p className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {sellersList.length}
          </p>
          <p className="text-sm text-gray-500">Vendeurs</p>
        </div>
        <div className="card-kpi border-[#E30613]">
          <p className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {totalObjectives}
          </p>
          <p className="text-sm text-gray-500">Objectifs signés</p>
        </div>
        <div className="card-kpi border-green-500">
          <p className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {totalVN}
          </p>
          <p className="text-sm text-gray-500">Total VN</p>
        </div>
        <div className="card-kpi border-blue-500">
          <p className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {totalVO}
          </p>
          <p className="text-sm text-gray-500">Total VO</p>
        </div>
      </div>

      {/* Sellers List */}
      <div className="card-dashboard" data-testid="history-table">
        {sellersList.length > 0 ? (
          <div className="space-y-2">
            {/* Header */}
            <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 rounded-lg font-medium text-sm text-gray-600">
              <div className="col-span-4">Vendeur</div>
              {user?.role === "admin" && <div className="col-span-2">Concession</div>}
              <div className={`${user?.role === "admin" ? "col-span-1" : "col-span-2"} text-center`}>Mois signés</div>
              <div className={`${user?.role === "admin" ? "col-span-2" : "col-span-2"} text-center`}>Total VN</div>
              <div className={`${user?.role === "admin" ? "col-span-2" : "col-span-2"} text-center`}>Total VO</div>
              <div className={`${user?.role === "admin" ? "col-span-1" : "col-span-2"} text-center`}>Financement</div>
            </div>

            {/* Seller rows */}
            {sellersList.map((seller) => (
              <Collapsible
                key={seller.seller_id}
                open={expandedSellers[seller.seller_id]}
                onOpenChange={() => toggleSeller(seller.seller_id)}
              >
                <CollapsibleTrigger asChild>
                  <div 
                    className="grid grid-cols-12 gap-4 px-4 py-4 bg-white border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    data-testid={`seller-row-${seller.seller_id}`}
                  >
                    <div className="col-span-4 flex items-center gap-3">
                      {expandedSellers[seller.seller_id] ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                      <div className="w-10 h-10 rounded-full bg-[#E30613] flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-medium">{seller.seller_name}</span>
                    </div>
                    {user?.role === "admin" && (
                      <div className="col-span-2 flex items-center text-sm text-gray-600">
                        {seller.concession_name}
                      </div>
                    )}
                    <div className={`${user?.role === "admin" ? "col-span-1" : "col-span-2"} flex items-center justify-center`}>
                      <Badge variant="secondary" className="bg-gray-100">
                        {seller.count} mois
                      </Badge>
                    </div>
                    <div className={`${user?.role === "admin" ? "col-span-2" : "col-span-2"} flex items-center justify-center`}>
                      <span className="text-xl font-bold text-green-600">{seller.total_vn}</span>
                    </div>
                    <div className={`${user?.role === "admin" ? "col-span-2" : "col-span-2"} flex items-center justify-center`}>
                      <span className="text-xl font-bold text-blue-600">{seller.total_vo}</span>
                    </div>
                    <div className={`${user?.role === "admin" ? "col-span-1" : "col-span-2"} flex items-center justify-center`}>
                      <span className="font-semibold text-gray-700">{seller.total_financing.toLocaleString()}€</span>
                    </div>
                  </div>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="ml-8 mt-2 mb-4 border-l-2 border-[#E30613] pl-4">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="text-xs">Mois</TableHead>
                          <TableHead className="text-xs text-center">VN</TableHead>
                          <TableHead className="text-xs text-center">VO</TableHead>
                          <TableHead className="text-xs text-center">Financement</TableHead>
                          <TableHead className="text-xs">Signé le</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {seller.objectives.map((obj) => (
                          <TableRow key={obj.id} className="text-sm">
                            <TableCell className="font-medium">
                              {monthNames[obj.month]} {obj.year}
                            </TableCell>
                            <TableCell className="text-center">{obj.vn_target}</TableCell>
                            <TableCell className="text-center">{obj.vo_target}</TableCell>
                            <TableCell className="text-center">{obj.financing_target?.toLocaleString()}€</TableCell>
                            <TableCell className="text-gray-500">
                              {obj.manager_signed_at 
                                ? new Date(obj.manager_signed_at).toLocaleDateString('fr-FR')
                                : obj.updated_at
                                  ? new Date(obj.updated_at).toLocaleDateString('fr-FR')
                                  : '-'
                              }
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Total row */}
                        <TableRow className="bg-gray-100 font-bold">
                          <TableCell>Total {filterYear}</TableCell>
                          <TableCell className="text-center text-green-600">{seller.total_vn}</TableCell>
                          <TableCell className="text-center text-blue-600">{seller.total_vo}</TableCell>
                          <TableCell className="text-center">{seller.total_financing.toLocaleString()}€</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <HistoryIcon className="empty-state-icon" />
            <p>Aucun objectif signé trouvé pour {filterYear}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
